import Moment from 'moment-timezone'
import {round, floor, lowerFirst, reduce, map, filter, union, isUndefined, isArray} from 'lodash'

const defaultValues = {
  timeRange: '-30 days',
  startDate: null,
  endDate: null,
  currency: 'USD',
  dieselPrice: 4,
  dieselUnit: 'us_gallon',
  weightFormat: 'imperial',
  gallonsPerHour: 1,
  dieselRuntimeTarget: 50,
  stagingRuntimeTarget: 20
}

const CO2_EMISSIONS_KG_PER_LITER = 2.692
const TREE_CO2_ABSORPTION_KG_PER_YEAR = 167
const PLANE_CO2_EMISSIONS_KG_PER_DOMESTIC_FLIGHT = 250
const LBS_PER_KG = 2.20462
const LITER_PER_US_GALLON = 3.785411784
const LITER_PER_UK_GALLON = 4.54609

const SustainabilityMixin = {
  computed: {
    /**
     * Get all of the sustainability parameters that are changeable through the parameter tweaker component. This
     * will try to obtain the parameters stored in the user's app storage first, then fall back to a customer setting
     * if there is one, and finally falling back to the defaults declared in defaultValues above.
     *
     * We return all fields as a single object (to group together and make explicit what these parameters are for.)
     * This means that all fields will get recomputed if a single dependent value changes anywhere, but this shouldn't
     * be a big deal. If it ends up a big deal we can add separate computed values instead, at the expense of
     * polluting the component namespace a bit.
     */
    sustainabilityParams () {
      return {
        timeRange: this.getStoredField('timeRange', this.customerContext),
        startDate: this.getStoredField('startDate'),
        endDate: this.getStoredField('endDate'),
        currency: this.getStoredField('currency', this.customerContext),
        dieselPrice: this.getStoredField('dieselPrice', this.customerContext),
        dieselUnit: this.getStoredField('dieselUnit', this.customerContext),
        weightFormat: this.getStoredField('weightFormat', this.customerContext),
        gallonsPerHour: this.getStoredField('gallonsPerHour', this.customerContext),
        dieselRuntimeTarget: this.getStoredField('dieselRuntimeTarget', this.customerContext),
        stagingRuntimeTarget: this.getStoredField('stagingRuntimeTarget', this.customerContext)
      }
    },
    dieselUnitLabel () {
      switch (this.sustainabilityParams.dieselUnit) {
        case 'us_gallon':
        case 'uk_gallon':
          return 'gallon'
        default:
          return 'liter'
      }
    },
    dieselUnitLabelShort () {
      switch (this.sustainabilityParams.dieselUnit) {
        case 'us_gallon':
        case 'uk_gallon':
          return 'diesel_unit_gal'
        default:
          return 'diesel_unit_l'
      }
    },
    weightFormatLabel () {
      switch (this.sustainabilityParams.weightFormat) {
        case 'metric':
          return 'kg CO₂'
        case 'imperial':
          return 'lbs CO₂'
        default:
          return ''
      }
    },
    currencySymbol () {
      switch (this.sustainabilityParams.currency) {
        case 'USD':
        case 'CAD':
        case 'AUD':
          return '$'
        case 'GBP':
          return '£'
        case 'EUR':
          return '€'
        case 'KRW':
          return '₩'
        default:
          return '$'
      }
    },
    currencyIcon () {
      switch (this.sustainabilityParams.currency) {
        case 'USD':
        case 'CAD':
        case 'AUD':
          return 'dollar-sign'
        case 'GBP':
          return 'pound-sign'
        case 'EUR':
          return 'euro-sign'
        case 'KRW':
          return 'won-sign'
        default:
          return 'dollar-sign'
      }
    },
    summaryString () {
      let timeString
      if (this.sustainabilityParams.timeRange !== 'custom') {
        switch (this.sustainabilityParams.timeRange) {
          case '-30 days':
            timeString = this.$t('past_30_days')
            break
          case '-60 days':
            timeString = this.$t('past_60_days')
            break
          case '-90 days':
            timeString = this.$t('past_90_days')
            break
          case '-6 months':
            timeString = this.$t('past_6_months')
            break
          case '-1 year':
            timeString = this.$t('past_year')
        }
      } else {
        timeString = this.$t('date_to_date', {
          from: Moment.tz(this.sustainabilityParams.startDate, this.customerTimezone).format('l'),
          to: Moment.tz(this.sustainabilityParams.endDate, this.customerTimezone).format('l')
        })
      }
      return this.$t('sustainability_dashboard_ui.params_summary', {
        timeSpan: timeString,
        currencySymbol: this.currencySymbol,
        price: this.sustainabilityParams.dieselPrice,
        currency: this.sustainabilityParams.currency,
        dieselUnitLabel: lowerFirst(this.$tc(this.dieselUnitLabel, 1))
      })
    },
    runtimeString () {
      return this.$t('sustainability_dashboard_ui.runtime_summary', {
        dieselRuntimeTarget: this.sustainabilityParams.dieselRuntimeTarget
      })
    },
    stagingRuntimeString () {
      return this.$t('sustainability_dashboard_ui.runtime_summary', {
        dieselRuntimeTarget: this.sustainabilityParams.stagingRuntimeTarget
      })
    },
    customTimeRange () {
      return this.formData.timeRange === 'custom'
    },
  },
  methods: {
    /**
     * Gets the value of the given sustainability field first checking the SustainabilityModule store, and then
     * delegating to getDefaultField if nothing is found there.
     * @param field
     * @param customer
     * @returns {*}
     */
    getStoredField (field, customer) {
      // Try getting the value from the sustainability module
      const value = this.$store.getters['sustainabilityModule/' + field]
      if (value == null) {
        return this.getDefaultField(field, customer)
      }
      return value
    },
    /**
     * Gets the default value for the given field. Checks the users setting and then uses the hardcoded defaults from
     * the defaultValues object defined as a constant above.
     * Some fields will not have customer defaults, so the customer parameter is optional.
     * @param field
     * @param customer
     * @returns {*}
     */
    getDefaultField (field, customer = null) {
      let value
      if (customer != null) {
        value = customer.getSetting(field)
      }
      if (value == null) {
        value = defaultValues[field]
      }
      return value
    },

    /**
     * Given a quantity of diesel in liters, convert according to the customer's format.
     * @param quantity
     * @param unit
     * @param precision
     * @returns number
     */
    convertDieselFromLiters (quantity, precision = 2, unit) {
      unit = unit == null ? this.sustainabilityParams.dieselUnit : unit

      switch (unit) {
        case 'liter':
          return round(quantity, precision)
        case 'us_gallon':
          return round(quantity / LITER_PER_US_GALLON, precision)
        case 'uk_gallon':
          return round(quantity / LITER_PER_UK_GALLON, precision)
      }
    },

    /**
     * Given a quantity of diesel in liters, calculate the CO2 emissions, and output per the specified format.
     * @param litersDiesel
     * @param weightFormat
     * @param precision
     */
    getCO2Emissions (litersDiesel, precision = 2, weightFormat) {
      weightFormat = weightFormat == null ? this.sustainabilityParams.weightFormat : weightFormat

      let result = litersDiesel * CO2_EMISSIONS_KG_PER_LITER
      if (weightFormat === 'imperial') {
        result *= LBS_PER_KG
      }
      return round(result, precision)
    },

    formatCurrency (value, symbol = null) {
      const formatter = Intl.NumberFormat(this.$i18n.locale, { minimumFractionDigits: 2 })
      const result = formatter.format(round(value, 2))
      if (symbol != null) {
        return symbol + result
      }
      return result
    },

    formatWholeCurrency (value, symbol = null) {
      const formatter = Intl.NumberFormat(this.$i18n.locale, { minimumFractionDigits: 0 })
      const result = formatter.format(round(value, 0))
      if (symbol != null) {
        return symbol + result
      }
      return result
    },

    getTreesEstimate (litersDiesel) {
      return floor(litersDiesel * CO2_EMISSIONS_KG_PER_LITER / TREE_CO2_ABSORPTION_KG_PER_YEAR)
    },

    getFlightsEstimate (litersDiesel) {
      return floor(litersDiesel * CO2_EMISSIONS_KG_PER_LITER / PLANE_CO2_EMISSIONS_KG_PER_DOMESTIC_FLIGHT)
    },
    // Fetch customer features and filter customers based on the sustainability feature
    async getCustomersWithSustainabilityFeature (customers) {
      const customerIds = customers.map(customer => customer.id)
      const response = await this.$httpAdapter.get('/sustainability-insights/customer-has-sustainability', {
        params: {
          customer_ids: customerIds.join(',')
        }
      })
      const customerIdsWithSustainability = response.data

      return customers.filter(customer => customerIdsWithSustainability.includes(customer.id))
    },
    // We want to expand the customer ids of any parent customers in the list, but not double count them if any of
    // the children are also present. Expand each customer if possible and merge in any children via union.
    async getExpandedCustomers (selectedCustomers, customers) {
      if (this.isRootUser) {
        return customers
      }
      const expandedCustomerIds = reduce(selectedCustomers, (result, customer) => {
        const customerIds = map(filter(customers, x => x.parent_id === customer.id), x => x.id)
        customerIds.push(customer.id)
        return union(result, customerIds)
      }, [])
      const expandedCustomers = filter(customers, customer => expandedCustomerIds.includes(customer.id))
      return await this.getCustomersWithSustainabilityFeature(expandedCustomers)
    },

    async getSustainabilityCustomers (customers) {
      if (this.isRootUser) {
        return customers
      }
      // Filter out customers that do not have a parent_id
      const customersWithParentId = customers.filter(customer => customer.parent_id !== null)
      const customerIds = customersWithParentId.map(customer => customer.id)
      const response = await this.$httpAdapter.get('/sustainability-insights/customer-has-sustainability', {
        params: {
          customer_ids: customerIds.join(',')
        }
      })
      const customerIdsWithSustainability = response.data
      return customersWithParentId.filter(customer => customerIdsWithSustainability.includes(customer.id))
    },

    getCustomerIds (customers) {
      return customers.map(customer => customer.id);
    }
  }
}

export default SustainabilityMixin
